import { useMemo, useState } from "react";
import { useLocation } from "react-router";
import {
  AlertCircle,
  LayoutGrid,
  MousePointerClick,
  RefreshCw,
  Rocket,
  Search,
  Smartphone,
  Sparkles,
  X,
} from "lucide-react";
import { useTemplates } from "../../lib/queries";
import { ALL_TEMPLATES_COVER, CATEGORIES, categoryLabel, findCategory, subcategoryLabel } from "../../lib/taxonomy";
import { ONLINE_STORE_COVER } from "../../lib/template-art";
import type { TemplateCategory, TemplateSubcategory } from "../../lib/api";
import { AppLayout } from "../../components/layout/AppLayout";
import { CategoryTile, TemplateCard } from "../../components/templates/TemplateCard";
import { EmptyState } from "../../components/ui/EmptyState";
import { Button } from "../../components/ui/Button";
import { SkeletonCard } from "../../components/ui/Skeleton";

/** A website category, every template, or online-store designs. */
type Filter = TemplateCategory | "all" | "store";

/**
 * Template gallery — served at /templates (public, PRD §15) and /app/templates
 * (inside the app). Photo tiles filter by category, chips by sub-category,
 * and a search box narrows by name or description. The whole catalog loads
 * once; filtering happens here.
 */
export function TemplatesPage() {
  const location = useLocation();
  const inApp = location.pathname.startsWith("/app");
  const [filter, setFilter] = useState<Filter>("all");
  const [subcategory, setSubcategory] = useState<TemplateSubcategory | null>(null);
  const [search, setSearch] = useState("");

  const { data: templates, isPending, error, refetch } = useTemplates();
  const category = findCategory(filter);
  const chooseFilter = (next: Filter) => {
    setFilter(next);
    setSubcategory(null);
  };

  const previewBase = inApp ? "/app/templates" : "/templates";
  const query = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      (templates ?? []).filter(
        (template) =>
          (filter === "all" ||
            (filter === "store" ? template.kind === "store" : template.category === filter)) &&
          (!subcategory || template.subcategory === subcategory) &&
          (!query ||
            template.name.toLowerCase().includes(query) ||
            template.description.toLowerCase().includes(query) ||
            categoryLabel(template.category).toLowerCase().includes(query) ||
            subcategoryLabel(template.subcategory).toLowerCase().includes(query) ||
            (template.categories ?? []).some((c) => c.includes(query))),
      ),
    [templates, query, filter, subcategory],
  );

  const heading =
    filter === "all"
      ? "All templates"
      : filter === "store"
        ? "Online store templates"
        : `${subcategory ? subcategoryLabel(subcategory) : category?.label} templates`;
  const filtered = filter !== "all" || query !== "";

  return (
    <AppLayout>
      <main className="page page--wide">
        <section className="tpl-hero">
          <span className="pill">
            <Sparkles size={14} aria-hidden="true" />
            Template gallery
          </span>
          <h1>
            A beautiful website for <em>every craft</em>
          </h1>
          <p>
            Designer-made starting points for professionals, artisans, developers and
            creatives, plus online stores. Pick one, make it yours, and publish in minutes.
          </p>
          <div className="tpl-search" role="search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              aria-label="Search templates"
              placeholder="Search by name or kind of business"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </section>

        <div className="cat-rail" role="group" aria-label="Filter by category">
          <CategoryTile
            image={ALL_TEMPLATES_COVER}
            label="All"
            hint="Every design"
            pressed={filter === "all"}
            onClick={() => chooseFilter("all")}
          />
          {CATEGORIES.map((option) => (
            <CategoryTile
              key={option.slug}
              image={option.cover}
              label={option.label}
              hint={option.hint}
              pressed={filter === option.slug}
              onClick={() => chooseFilter(option.slug)}
            />
          ))}
          <CategoryTile
            image={ONLINE_STORE_COVER}
            label="Online stores"
            hint="Products, cart and checkout"
            pressed={filter === "store"}
            onClick={() => chooseFilter("store")}
          />
        </div>

        {category ? (
          <div className="chip-row" role="group" aria-label={`${category.label} sub-categories`}>
            <button
              type="button"
              className={`chip${subcategory === null ? " chip--active" : ""}`}
              aria-pressed={subcategory === null}
              onClick={() => setSubcategory(null)}
            >
              All {category.label.toLowerCase()}
            </button>
            {category.subcategories.map((sub) => (
              <button
                key={sub.slug}
                type="button"
                className={`chip${subcategory === sub.slug ? " chip--active" : ""}`}
                aria-pressed={subcategory === sub.slug}
                onClick={() => setSubcategory(sub.slug)}
              >
                {sub.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="tpl-toolbar">
          <h2>
            {heading}
            {!isPending && !error ? (
              <span className="tpl-toolbar__count">
                {visible.length} {visible.length === 1 ? "design" : "designs"}
              </span>
            ) : null}
          </h2>
          {filtered ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                chooseFilter("all");
                setSearch("");
              }}
            >
              <X size={14} aria-hidden="true" />
              Clear filters
            </Button>
          ) : null}
        </div>

        {isPending ? (
          <div className="tpl-grid" aria-busy="true" aria-label="Loading templates">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : null}

        {error ? (
          <div role="alert">
            <EmptyState
              icon={<AlertCircle size={28} />}
              title="Could not load the template catalog."
              description="Check your connection and try again."
              action={
                <Button variant="secondary" onClick={() => refetch()}>
                  <RefreshCw size={16} />
                  Try again
                </Button>
              }
            />
          </div>
        ) : null}

        {!isPending && !error && visible.length === 0 ? (
          <EmptyState
            icon={<LayoutGrid size={28} />}
            title="No templates match"
            description={
              query
                ? `Nothing matches “${search.trim()}”. Try another word or clear the filters.`
                : "No templates here yet. Try another category."
            }
          />
        ) : null}

        <div className="tpl-grid">
          {visible.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              previewHref={`${previewBase}/${template.id}`}
              useHref={`/app/onboarding?template=${template.id}`}
            />
          ))}
        </div>

        <section className="tpl-perks" aria-label="Why start from a template">
          <div className="tpl-perks__item">
            <MousePointerClick size={20} aria-hidden="true" />
            <div>
              <strong>Edit everything</strong>
              <span>Change text, photos, colors and pages — no code.</span>
            </div>
          </div>
          <div className="tpl-perks__item">
            <Smartphone size={20} aria-hidden="true" />
            <div>
              <strong>Looks great on phones</strong>
              <span>Every design adapts to desktop, tablet and mobile.</span>
            </div>
          </div>
          <div className="tpl-perks__item">
            <Rocket size={20} aria-hidden="true" />
            <div>
              <strong>Live in minutes</strong>
              <span>Publish to your own Zirro address with one click.</span>
            </div>
          </div>
        </section>
      </main>
    </AppLayout>
  );
}
